import { useCallback, useMemo } from 'react';

import type { I18nUtilities } from '@change-corgi/core-react/i18n';
import { useI18n } from '@change-corgi/core-react/i18n';
import { useNavigate } from '@change-corgi/core-react/router';
import { useTracking } from '@change-corgi/core-react/tracking';

import { usePsfPromoteOrSharePageData } from '#src/app/pages/psf/promoteOrShare/pageContext';
import type { ShareChannel } from '#src/app/pages/psf/promoteOrShare/shared/types';
import type { FacebookGroupRecommendation } from '#src/app/pages/psf/shared/api';
import type { ShareAction } from '#src/app/shared/hooks/share';

import { usePromoteOrShareActionUrls } from '../usePromoteOrShareActionUrls';
import type { ShareFileMethod } from '../useShareFile';
import { type SlideUpQrCode, useSlideUpQrCode } from '../useSlideUpQrCode';

import { mapButtonConfigs } from './mapButtonConfigs';
import { usePsfShareAvailableShareActions } from './share';
import type { QrShareSource } from './share/success';
import { type QrCodeTracking, useQrCodeTracking } from './share/tracking';
import { type ChannelAwareCta, useChannelAwareCta } from './useChannelAwareCta';
import {
	type FacebookPostSheetTap,
	type UseFacebookPostSheetResult,
	useFacebookPostSheet,
} from './useFacebookPostSheet';
import { useFacebookPostSheetEligibility } from './useFacebookPostSheetEligibility';
import { useFacebookPostSheetInterception } from './useFacebookPostSheetInterception';
import { type QrCodePoster, useQrCodePoster } from './useQrCodePoster';
import { type FacebookPostSheetArm, useTreatFacebookPostSheetExperiment } from './useTreatFacebookPostSheetExperiment';
import { type QrCodeLayout, useTreatQrCodeExperiment } from './useTreatQrCodeExperiment';

type TranslateLabel = (i18n: I18nUtilities) => string;

// EXPERIMENT: psf_promote_or_share_qr_code_experiment — remove with experiment cleanup
/** Called after a QR Save or Print delivers its file, with where it came from and how it was delivered. */
export type OnQrCodeShared = (source: QrShareSource, method: ShareFileMethod) => void;

type ButtonConfig = {
	ShareAction: ShareAction['ShareAction'];
	channel: ShareChannel;
	label: TranslateLabel;
	successLabel?: TranslateLabel;
	secondaryLabel?: TranslateLabel;
	url: string;
	shortUrl?: string;
};

type UseActionItemsResult = ModelHookResult<
	{
		buttonConfigs: readonly ButtonConfig[];
		continueButtonProps: {
			label: string;
			action: () => void;
		};
		shareVisualUrl: string | undefined;
		channelAwareCta: ChannelAwareCta;
		facebookPostSheet: UseFacebookPostSheetResult;
		/** The sheet a treated Facebook tap opens, or `null` for production. `ShareSlideUp` renders its steps. */
		facebookPostSheetArm: FacebookPostSheetArm | null;
		/** The group recommendations the `variant_2` group cards list, from the fetch that eligibility read. */
		facebookPostSheetGroups: readonly FacebookGroupRecommendation[];
		// EXPERIMENT: psf_promote_or_share_qr_code_experiment — remove with experiment cleanup
		/** What the QR code experiment's arm adds to the slide-up, or `null` for production. */
		qrCodeLayout: QrCodeLayout | null;
		// EXPERIMENT: psf_promote_or_share_qr_code_experiment — remove with experiment cleanup
		/** The QR view's code and Save file, fetched on the first open in a treatment arm. */
		qrCode: SlideUpQrCode;
		/** The QR tile's, QR view's, and Save's events. The poster view tracks its own. */
		qrCodeTracking: QrCodeTracking;
		/** What the `variant_2` poster view builds the Flyer A from. */
		qrCodePoster: QrCodePoster;
		/** Redirects to the share page after a QR Save (both treatment arms) or a Print (`variant_2`). */
		onQrCodeShared: OnQrCodeShared;
		/** Cancels a pending QR download redirect, on Print poster and on Back from the poster view (`variant_2`). */
		cancelQrRedirect: () => void;
	},
	{
		handleOpenShareSlideUp: (options?: OpenShareSlideUpOptions) => void;
		handleCloseShareSlideUp: () => void;
	}
>;

type OpenShareSlideUpOptions = Readonly<{
	/**
	 * EXPERIMENT: constituent_psf_experiment. The sheet opens on the local-signer share screen instead of
	 * the channel grid, so the QR code experiment (which changes that grid) neither treats nor fetches.
	 */
	skipQrCodeExperiment?: boolean;
}>;

type Params = {
	/** Opens the Facebook post sheet for a treated Facebook tap. `ShareSlideUp` owns the sheet's open state. */
	onOpenFacebookPostSheet?: (tap: FacebookPostSheetTap) => void;
};

// oxlint-disable-next-line max-statements
export function useShareActions({ onOpenFacebookPostSheet }: Params = {}): UseActionItemsResult {
	const {
		shareActions: availableShareActions,
		shareOptions,
		redirectAfterQrShare: onQrCodeShared,
		cancelQrRedirect,
	} = usePsfShareAvailableShareActions();
	const {
		data: { petition, isShareAssetsEnabled },
	} = usePsfPromoteOrSharePageData();
	const channelAwareCta = useChannelAwareCta();
	// EXPERIMENT: psf_promote_or_share_qr_code_experiment — remove with experiment cleanup
	const { treatOnSlideUpOpen: treatQrCodeOnSlideUpOpen, qrCodeLayout } = useTreatQrCodeExperiment();
	const { qrCode, startFetch: startQrCodeFetch } = useSlideUpQrCode(qrCodeLayout !== null);
	const qrCodeTracking = useQrCodeTracking(qrCode.shareId);
	const qrCodePoster = useQrCodePoster();
	const mappedButtonConfigs = useMemo(() => mapButtonConfigs(availableShareActions), [availableShareActions]);

	const shareVisualUrl = isShareAssetsEnabled ? petition.bestShareVisual?.url : undefined;

	const {
		isEligible: isFacebookPostDataEligible,
		caption: facebookPostCaption,
		groups: facebookPostGroups,
		startFetch: startFacebookPostDataFetch,
		reportTap: reportFacebookTap,
	} = useFacebookPostSheetEligibility(petition.slug);
	const facebookPostSheet = useFacebookPostSheet({ caption: facebookPostCaption, shareOptions });
	const {
		sheetArm: facebookPostSheetArm,
		treatsAtTap: treatsFacebookPostSheetAtTap,
		treatAtTap: treatFacebookPostSheetAtTap,
	} = useTreatFacebookPostSheetExperiment(isFacebookPostDataEligible);

	const { copyAtTap: copyFacebookPostAtTap, tapFacebook, shareAtTap: shareFacebookAtTap } = facebookPostSheet;
	const { onBeforeShare } = shareOptions;
	const onFacebookTap = useCallback(() => onBeforeShare?.('facebook'), [onBeforeShare]);
	const onFacebookPostSheetTap = useCallback(
		(tap: FacebookPostSheetTap) => {
			tapFacebook(tap);
			onOpenFacebookPostSheet?.(tap);
		},
		[tapFacebook, onOpenFacebookPostSheet],
	);
	const buttonConfigsWithFacebookPostSheet = useFacebookPostSheetInterception(
		mappedButtonConfigs,
		useMemo(
			() => ({
				onTap: onFacebookTap,
				treatAtTap: treatFacebookPostSheetAtTap,
				reportTap: reportFacebookTap,
				// Eligibility already requires a ready caption, so this only guards a missing Facebook link.
				sheetArm: facebookPostSheet.caption !== null ? facebookPostSheetArm : null,
				// Same guard: `shareAtTap` needs the Facebook link, and the pipeline runs without it.
				treatsAtTap: facebookPostSheet.caption !== null && treatsFacebookPostSheetAtTap,
				shareAtTap: shareFacebookAtTap,
				// EXPERIMENT: psf_promote_or_share_facebook_post_sheet_experiment. Both sheets copy at the Facebook
				// tap, for the copied pill they open with.
				copyAtTap: copyFacebookPostAtTap,
				onSheetTap: onFacebookPostSheetTap,
			}),
			[
				onFacebookTap,
				treatFacebookPostSheetAtTap,
				reportFacebookTap,
				facebookPostSheet.caption,
				facebookPostSheetArm,
				treatsFacebookPostSheetAtTap,
				shareFacebookAtTap,
				copyFacebookPostAtTap,
				onFacebookPostSheetTap,
			],
		),
	);

	return {
		data: {
			buttonConfigs: buttonConfigsWithFacebookPostSheet,
			continueButtonProps: useContinueButtonProps(),
			shareVisualUrl,
			channelAwareCta,
			facebookPostSheet,
			facebookPostSheetArm,
			facebookPostSheetGroups: facebookPostGroups,
			qrCodeLayout,
			qrCode,
			qrCodeTracking,
			qrCodePoster,
			onQrCodeShared,
			cancelQrRedirect,
		},
		actions: useHandlers({ treatQrCodeOnSlideUpOpen, startFacebookPostDataFetch, startQrCodeFetch }),
	};
}

type HandlersParams = {
	// EXPERIMENT: psf_promote_or_share_qr_code_experiment — remove with experiment cleanup
	treatQrCodeOnSlideUpOpen: () => Promise<void>;
	startFacebookPostDataFetch: () => void;
	// EXPERIMENT: psf_promote_or_share_qr_code_experiment — remove with experiment cleanup
	startQrCodeFetch: () => void;
};

function useHandlers({ treatQrCodeOnSlideUpOpen, startFacebookPostDataFetch, startQrCodeFetch }: HandlersParams) {
	const track = useTracking();

	return {
		handleOpenShareSlideUp: useCallback(
			({ skipQrCodeExperiment = false }: OpenShareSlideUpOptions = {}) => {
				void track('combo_psf_share');
				void track('combo_psf_share_slide_up_opened');
				startFacebookPostDataFetch();

				if (skipQrCodeExperiment) return;
				// EXPERIMENT: psf_promote_or_share_qr_code_experiment — remove with experiment cleanup
				// Every eligible, non-override opener, in every arm: the treatment changes the grid they see.
				// See useTreatQrCodeExperiment.
				void treatQrCodeOnSlideUpOpen();
				startQrCodeFetch();
			},
			[track, treatQrCodeOnSlideUpOpen, startFacebookPostDataFetch, startQrCodeFetch],
		),
		handleCloseShareSlideUp: useCallback(() => {
			void track('combo_psf_share_slide_up_closed');
		}, [track]),
	};
}

function useContinueButtonProps() {
	const track = useTracking();
	const { translate } = useI18n();
	const navigate = useNavigate();
	const {
		data: { shareButtonUrl: continueUrl },
	} = usePromoteOrShareActionUrls();

	return {
		label: translate('corgi.psf.share.footer.continue'),
		action: useCallback(() => {
			void track('combo_psf_share_slide_up_continue');
			navigate(continueUrl);
		}, [continueUrl, navigate, track]),
	};
}
